import { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogContent,
  Typography,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { addPointClient, getCodeClient } from "../../services/api.service";

// ✅ Slide Animation for Dialog
const Transition = Slide as React.ComponentType<
  TransitionProps & { children: React.ReactElement }
>;

const PromoCodeForm: React.FC = () => {
  const [form, setForm] = useState({
    username: "",
    code: "",
    captcha: "",
  });

  const [errors, setErrors] = useState({
    username: false,
    code: false,
    captcha: false,
  });
  const [point, setPoint] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] =  useState(false);

  const [captchaText, setCaptchaText] = useState("");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);

  // ✅ Change handler
  const handleChange = async (field: string, value: string) => {
    setForm({ ...form, [field]: value });
    setErrors({ ...errors, [field]: false });
  };
  // ✅ Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = {
      username: !form.username,
      code: !form.code,
      captcha: !form.captcha,
    };
    setErrors(newErrors);
    const isValid = Object.values(newErrors).every((v) => !v);
    if (!isValid) {
      // กรอกไม่ครบ
      setErrorMessage("กรุณากรอกข้อมูลให้ครบถ้วน");
      setOpenError(true);
      return;
    }
    if (form.captcha === captchaText) {
      try {
        setLoading(true);
        let codeResult = await getCodeClient(form.code, form.captcha, "");
        console.log("codeResult: ", codeResult);
        if (codeResult?.status_code !== 200 || !codeResult?.valid) {
          throw new Error(codeResult?.title_mess?.th || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        }
        let addPoint = await addPointClient(form.username, form.code);
        if (addPoint.status_code !== 200 || !addPoint.valid) {
          // addPoint.text_mess[intl.locale]
          throw new Error(addPoint?.text_mess?.th || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        } else {
          setPoint(addPoint?.point ?? 0)
          setOpenSuccess(true);
        }
      } catch (error: any) {
        generateCaptcha();
        setErrorMessage(error.message);
        setForm({ username: "", code: "", captcha: "" });
        return setOpenError(true);
      }finally{
        setLoading(false);
      }
      //setOpenSuccess(true);
    } else {
      // ❌ ผิด
      setOpenError(true);
      setErrorMessage("รหัสแคปช่าไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง");
      generateCaptcha(); // ✅ สุ่มใหม่ทันทีเมื่อผิด
      setForm({ ...form, captcha: "" });
    }
  };

  const handleCloseAll = () => {
    setOpenSuccess(false);
    setOpenError(false);
  };

  // ✅ Generate random CAPTCHA
  const generateCaptcha = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let captcha = "";
    for (let i = 0; i < 5; i++) { // ✅ 5 ตัวอักษรผสม
      captcha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(captcha);
  };

  // ✅ Render CAPTCHA to Canvas
  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // พื้นหลังแบบ gradient
    const gradient = ctx.createLinearGradient(0, 0, w, h);
    gradient.addColorStop(0, "#FFCDD2");
    gradient.addColorStop(1, "#F8BBD0");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    // ใส่เส้นสุ่มกันบอท
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(${Math.random() * 255},0,0,0.4)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * w, Math.random() * h);
      ctx.lineTo(Math.random() * w, Math.random() * h);
      ctx.stroke();
    }

    // ตัวหนังสือ CAPTCHA
    ctx.font = "bold 22px Arial";
    ctx.fillStyle = "#D32F2F";
    ctx.textBaseline = "middle";
    for (let i = 0; i < captchaText.length; i++) {
      const letter = captchaText[i];
      const x = 10 + i * 18;
      const y = h / 2 + (Math.random() * 6 - 3);
      const angle = (Math.random() - 0.5) * 0.4;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillText(letter, 0, 0);
      ctx.restore();
    }

    // จุด noise
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.3})`;
      ctx.beginPath();
      ctx.arc(Math.random() * w, Math.random() * h, 1.2, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [captchaText]);

  return (
    <>
      {/* ✅ Main Form */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          background: "linear-gradient(180deg, #FFF8E1 0%, #FFF3C4 100%)",
          borderRadius: "20px",
          p: 3,
          width: { xs: "85%", md: "450px" },
          mx: "auto",
          boxShadow:
            "0 0 35px 20px rgba(255, 140, 0, 0.5), inset 0 0 12px rgba(255,255,255,0.5)",
        }}
      >
        {/* Username */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            component="img"
            draggable="false"
            src="/imgs/user.png?v12"
            alt="user"
            sx={{ width: 28, height: 28, mr: 1.5 }}
          />
          <TextField
            fullWidth
            required
            placeholder="กรอกยูสเซอร์"
            variant="outlined"
            size="small"
            value={form.username}
            error={errors.username}
            helperText={errors.username && "กรุณากรอกยูสเซอร์"}
            onChange={(e) => handleChange("username", e.target.value)}
            autoComplete="off"
            sx={{
              bgcolor: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "1rem",
              },
            }}
          />
        </Box>

        {/* Code */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            component="img"
            draggable="false"
            src="/imgs/Promotion.png"
            alt="code"
            sx={{ width: 28, height: 28, mr: 1.5 }}
          />
          <TextField
            fullWidth
            required
            placeholder="กรอกโค้ดฟรี"
            variant="outlined"
            size="small"
            value={form.code}
            error={errors.code}
            helperText={errors.code && "กรุณากรอกโค้ดฟรี"}
            onChange={(e) => handleChange("code", e.target.value)}
            autoComplete="off"
            sx={{
              bgcolor: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "1rem",
              },
            }}
          />
        </Box>

        {/* Captcha */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Box
            component="img"
            draggable="false"
            src="/imgs/Captcha.png"
            alt="captcha"
            sx={{ width: 28, height: 28, mr: 1.5 }}
          />
          <Box sx={{ display: "flex", flex: 1 }}>
            <Box
              onClick={generateCaptcha}
              sx={{
                width: 90,
                height: 40,
                borderRadius: "8px",
                mr: 1.5,
                background:
                  "linear-gradient(90deg, #FFCDD2 0%, #F8BBD0 50%, #FFCDD2 100%)",
                boxShadow: "inset 0 0 5px rgba(255,0,0,0.3)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "#D32F2F",
                fontWeight: "bold",
                fontSize: "1.2rem",
                cursor: "pointer",
              }}
            >
              <canvas ref={canvasRef} width={100} height={40} />
            </Box>
            <TextField
              fullWidth
              required
              placeholder="กรอกเลขยืนยัน"
              variant="outlined"
              size="small"
              value={form.captcha}
              error={errors.captcha}
              helperText={errors.captcha && "กรุณากรอกเลขยืนยัน"}
              onChange={(e) => handleChange("captcha", e.target.value)}
              autoComplete="off"
              sx={{
                bgcolor: "#fff",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "1rem",
                },
              }}
            />
          </Box>
        </Box>

        {/* Submit Button */}
        <Button
          fullWidth
          type="submit"
          variant="contained"
          loading={loading}
          sx={{
            background:
              "linear-gradient(270deg, #B22418 1.32%, #FF1200 50.02%, #BA2619 98.71%)",
            color: "#fff",
            fontWeight: "bold",
            fontSize: "1.1rem",
            borderRadius: "10px",
            py: 1.2,
            boxShadow: "0 5px 10px rgba(255, 61, 0, 0.3)",
            "&:hover": {
              background:
                "linear-gradient(270deg, #BA2619 1.32%, #FF1200 50.02%, #B22418 98.71%)",
            },
          }}
        >
          ยืนยัน
        </Button>
      </Box>

      {/* 🎉 Success Dialog */}
      <Dialog
        open={openSuccess}
        onClose={handleCloseAll}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            borderRadius: "20px",
            textAlign: "center",
            p: 3,
            background:
              "linear-gradient(180deg, #FFF8E1 0%, #FFD54F 100%)",
            boxShadow: "0 0 25px rgba(255, 140, 0, 0.5)",
          },
        }}
      >
        <DialogContent>
          <Box
            component="img"
            src="/success.png"
            alt="success"
            draggable="false"
            sx={{ width: 120, mb: 2 }}
          />
          <Typography variant="h5" fontWeight="bold" color="#D32F2F">
            🎉 ยินดีด้วย! 🎉
          </Typography>
          <Typography mt={1} color="text.secondary">
            คุณได้รับเครดิตฟรี {point} เรียบร้อยแล้ว
          </Typography>

          <Button
            onClick={handleCloseAll}
            sx={{
              mt: 3,
              px: 4,
              borderRadius: "10px",
              background:
                "linear-gradient(90deg, #FF3D00 0%, #FF6D00 100%)",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #FF6D00 0%, #FF3D00 100%)",
              },
            }}
          >
            ปิด
          </Button>
        </DialogContent>
      </Dialog>

      {/* ❌ Error Dialog */}
      <Dialog
        open={openError}
        onClose={handleCloseAll}
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            borderRadius: "20px",
            textAlign: "center",
            p: 3,
            background:
              "linear-gradient(180deg, #FFE4E1 0%, #FFCDD2 100%)",
            boxShadow: "0 0 25px rgba(255, 0, 0, 0.4)",
          },
        }}
      >
        <DialogContent>
          <Box
            component="img"
            src="/error.png"
            alt="error"
            draggable="false"
            sx={{ width: 120, mb: 2 }}
          />
          <Typography variant="h5" fontWeight="bold" color="#C62828">
            ❌ ไม่สำเร็จ ❌
          </Typography>
          <Typography mt={1} color="text.secondary">
            {errorMessage ?? "กรุณากรอกข้อมูลให้ครบถ้วนหรือรหัสไม่ถูกต้อง"}
          </Typography>

          <Button
            onClick={handleCloseAll}
            sx={{
              mt: 3,
              px: 4,
              borderRadius: "10px",
              background:
                "linear-gradient(90deg, #D32F2F 0%, #FF5252 100%)",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #FF5252 0%, #D32F2F 100%)",
              },
            }}
          >
            ปิด
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PromoCodeForm;
